import { useCallback, useEffect, useMemo, useState } from 'react';
import bridge from '@libs/bridge';
import { fetchRaw } from '@libs/request';
import { LocalCache } from '@utils/cache';

const cache = LocalCache.getInstance(10 * 60 * 1000); // 10min缓存有效期
const settingsUrl = 'https://editor-api.capcutapi.com/service/settings/v3/';

interface fetchSettingResponse {
  data: {
    settings: Record<string, any>;
  };
}

const fetchRegion = async () => {
  const res = await bridge.app.getInfo({ type: ['device'] });
  const { header } = res.data.deviceInfo;
  const { region, loc } = header as { region?: string; loc?: string };

  return region || loc;
};

const getCacheKey = async () => {
  const deviceRegion = await fetchRegion();
  const { data } = await bridge.app.getAppInfo().catch(() => ({ data: {} }));
  const {
    appVersion = 'default',
    device_id = 'default',
    region = 'default',
  } = data as { appVersion: string; device_id: string; region: string };
  // 影响settings拉取结果的参数拼接成唯一key标识
  return `capcut_${appVersion}_${deviceRegion || region}_${device_id}`;
};

export async function fetchAllSettings() {
  const key = await getCacheKey();
  const localData = cache.getLocalData(key);
  if (localData) {
    return localData;
  }
  const result = await fetchRaw<fetchSettingResponse>({
    url: settingsUrl,
    method: 'GET',
    needCommonParams: true,
  }).catch(() => console.error);
  const newData = result?.data?.settings;
  if (newData) {
    cache.setLocalData(key, newData);
  }
  return newData || {};
}

export function useSettings() {
  const [settings, setSettings] = useState<Record<string, any>>();

  const fetch = useCallback(async () => {
    const data = await fetchAllSettings();
    setSettings(data);
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return useCallback(
    <T>(key: string) => {
      if (!settings) {
        return null;
      }
      return settings[key] as T;
    },
    [settings]
  );
}

export function useGetSettingsLoading(getSettings: Function) {
  const loading = useMemo(() => getSettings('capcut_access') === null, [getSettings]);
  return loading;
}
